/*
  Admin › Integrations › QuickBooks Desktop — READ ONLY (owner decision 9 Oct 2026; src/lib/qbDesktop.js,
  backend/routes/qb_desktop.py). The company's books stay in QuickBooks Desktop; the free QuickBooks Web Connector on
  the QuickBooks PC sends Sara what QuickBooks has (invoices, payments, items, customers, vendors, costs) every 30 min.

  - Set-up as four steps: switch on, Web Connector password (shown once), the .qwc file, what to do on the PC.
  - What was read: company, last run, counts per kind, the last runs with their errors; "Read everything again".
  - Projects ↔ QuickBooks customer:job links: suggestions by name, a search over every customer / job, Link / Unlink
    (each change undoable from the toast or History).
*/
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BookOpenCheck, CircleCheck, CircleDot, Copy, Download, KeyRound, Link2, Loader2, Lock, MonitorCog, RefreshCw,
  Search, ShieldCheck, TriangleAlert, Unlink,
} from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatDateTime, formatNumber, formatTimeAgo } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import {
  QB_DESKTOP_FEATURE, fetchQbCustomers, fetchQbProjectLinks, fetchQbStatus, fetchQwcFile, linkQbCustomer, newQbPassword,
  resyncQb,
} from '@/lib/qbDesktop';
import { useIntegrationStatus } from '@/hooks/useIntegrationStatus';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import { RowActions } from '@/components/ui/row-actions';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { notify } from '@/components/ui/notify';
import { AdminCallout, AdminCard, UpperLabel } from '@/components/admin/AdminUi';
import { copyText } from '@/components/admin/PasswordField';

const KIND_LABELS = [
  ['customer', 'Customers & jobs'], ['vendor', 'Vendors'], ['item', 'Items'], ['invoice', 'Invoices'],
  ['payment', 'Payments'], ['bill', 'Bills'], ['check', 'Checks'], ['card_charge', 'Card charges'],
  ['purchase_order', 'Purchase orders'],
];

const RUN_PILL = {
  ok: { tone: 'green', label: 'Read' },
  partial: { tone: 'amber', label: 'Read with errors' },
  failed: { tone: 'red', label: 'Failed' },
  stopped: { tone: 'amber', label: 'Stopped' },
  running: { tone: 'blue', label: 'Reading…' },
};

const readBlobText = (blob) => (typeof blob.text === 'function'
  ? blob.text()
  : new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  }));

/** The card's badge: off / waiting for the first run / last read / last run failed. */
export function qbDesktopPill(status) {
  if (!status) return null;
  if (status.status !== 'on') return { tone: 'gray', label: 'Off' };
  if (!status.set_up) return { tone: 'amber', label: 'Setup needed' };
  if (!status.last_success_at) return { tone: 'amber', label: 'Waiting for the first run' };
  if (status.last_run_status === 'failed') return { tone: 'red', label: 'Last run failed' };
  return { tone: 'green', label: `Read ${formatTimeAgo(status.last_success_at)}` };
}

function Step({ index, title, done, locked, detail, action, testId }) {
  const Icon = done ? CircleCheck : locked ? Lock : CircleDot;
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3" data-state={done ? 'done' : locked ? 'locked' : 'todo'} data-testid={testId}>
      <Icon className={cn('h-[18px] w-[18px] shrink-0', done ? 'text-status-green' : locked ? 'text-ink-4' : 'text-brand-ink')} aria-hidden="true" />
      <div className="min-w-0 flex-1 basis-[220px]">
        <p className={cn('text-[13.5px] font-semibold', locked ? 'text-ink-3' : 'text-ink')}>
          <span className="mr-1 font-normal text-ink-3">{index}</span>{title}
          <span className="sr-only"> — {done ? 'done' : locked ? 'locked' : 'not yet'}</span>
        </p>
        <div className="text-[12px] text-ink-3">{detail}</div>
      </div>
      {action ? <div className="ml-auto shrink-0 max-sm:ml-0 max-sm:basis-full max-sm:pl-[30px]">{action}</div> : null}
    </li>
  );
}

/** The Web Connector password, shown once after it was made. */
function NewPassword({ credentials, onDone }) {
  const copy = async (text, what) => {
    try {
      await copyText(text);
      notify.success(`${what} copied`);
    } catch (err) {
      notify.error(`Could not copy the ${what.toLowerCase()}`, { description: getErrorMessage(err) });
    }
  };
  return (
    <AdminCallout tone="navy" icon={KeyRound} title="Web Connector password — shown once" live="status" data-testid="qbd-new-password">
      <p>Type it in the Web Connector’s <b className="font-semibold text-ink">Password</b> column for “Sara PM (read only)”, then Save. A new password stops the old one at once.</p>
      <dl className="mt-2 grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1.5">
        <dt className="text-[12px] text-ink-3">User name</dt>
        <dd className="font-mono text-[13px] text-ink">{credentials.username}</dd>
        <dd><Button type="button" variant="ghost" size="sm" onClick={() => copy(credentials.username, 'User name')}><Copy aria-hidden="true" /> Copy</Button></dd>
        <dt className="text-[12px] text-ink-3">Password</dt>
        <dd className="break-all font-mono text-[13px] text-ink" data-testid="qbd-password-value">{credentials.password}</dd>
        <dd><Button type="button" variant="ghost" size="sm" onClick={() => copy(credentials.password, 'Password')} data-testid="qbd-copy-password"><Copy aria-hidden="true" /> Copy</Button></dd>
      </dl>
      <Button type="button" variant="outline" size="sm" className="mt-3 bg-surface" onClick={onDone}>I saved it in the Web Connector</Button>
    </AdminCallout>
  );
}

/** Projects ↔ QuickBooks customer:job — suggestions by name and a search over every customer / job. */
export function ProjectLinks({ reloadKey }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [customers, setCustomers] = useState(null);
  const [openId, setOpenId] = useState('');
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState('');
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    fetchQbProjectLinks()
      .then((res) => { if (alive) { setData(res); setError(''); } })
      .catch((err) => { if (alive) setError(getErrorMessage(err, 'Could not load the project links')); });
    return () => { alive = false; };
  }, [reloadKey, tick]);

  const openPicker = (projectId) => {
    setOpenId((current) => (current === projectId ? '' : projectId));
    setQuery('');
    if (!customers) {
      fetchQbCustomers().then((res) => setCustomers(res.customers || [])).catch(() => setCustomers([]));
    }
  };

  const link = async (row, qbId, fullName) => {
    setSaving(row.project_id);
    try {
      const result = await linkQbCustomer(row.project_id, qbId);
      setOpenId('');
      setTick((n) => n + 1);
      const undoId = result?.undo_id;
      notify.success(qbId ? `${row.name} linked to ${fullName}` : `${row.name} unlinked from QuickBooks`, undoId ? {
        undo: async () => {
          await api.post(`/undo/entries/${undoId}`);
          setTick((n) => n + 1);
        },
      } : undefined);
    } catch (err) {
      notify.error('Could not change the link', { description: getErrorMessage(err) });
    } finally {
      setSaving('');
    }
  };

  const matches = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return (customers || [])
      .filter((c) => words.every((w) => String(c.full_name || '').toLowerCase().includes(w)))
      .slice(0, 8);
  }, [customers, query]);

  if (error) return <p className="text-[13px] text-status-red-ink" data-testid="qbd-links-error">{error}</p>;
  if (!data) return <div className="h-16 animate-pulse rounded-xl bg-surface-2" aria-label="Loading project links" />;
  const linkedCount = data.projects.filter((p) => p.linked).length;
  return (
    <div data-testid="qbd-project-links">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <UpperLabel>Projects ↔ QuickBooks jobs</UpperLabel>
        <span className="text-[12px] text-ink-3">{formatNumber(linkedCount)} of {formatNumber(data.projects.length)} linked</span>
      </div>
      <p className="mt-1 text-[12.5px] text-ink-3">A linked project shows its QuickBooks invoices, payments and costs on its Invoicing tab.</p>
      <ul className="mt-2 divide-y divide-line-2 rounded-xl border border-line">
        {data.projects.map((row) => (
          <li key={row.project_id} className="px-3 py-2.5" data-testid={`qbd-link-${row.project_id}`}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <span className="min-w-0 flex-1 basis-[160px] truncate text-[13px] font-semibold text-ink">{row.name}</span>
              {row.linked ? (
                <>
                  <span className="inline-flex min-w-0 items-center gap-1 text-[12.5px] text-ink-2"><Link2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /><span className="truncate">{row.linked.full_name}</span></span>
                  <Button type="button" variant="ghost" size="sm" disabled={saving === row.project_id} onClick={() => link(row, null)} data-testid={`qbd-unlink-${row.project_id}`}>
                    <Unlink aria-hidden="true" /> Unlink
                  </Button>
                </>
              ) : (
                <>
                  {row.suggestions?.[0] ? (
                    <Button type="button" variant="outline" size="sm" disabled={saving === row.project_id}
                      onClick={() => link(row, row.suggestions[0].qb_id, row.suggestions[0].full_name)}
                      title={`Suggested by name (${Math.round(row.suggestions[0].score * 100)}% alike)`}
                      data-testid={`qbd-suggest-${row.project_id}`}>
                      <Link2 aria-hidden="true" /> Link to {row.suggestions[0].full_name}
                    </Button>
                  ) : <span className="text-[12.5px] text-ink-4">No match by name</span>}
                  <Button type="button" variant="ghost" size="sm" onClick={() => openPicker(row.project_id)} aria-expanded={openId === row.project_id} data-testid={`qbd-choose-${row.project_id}`}>
                    <Search aria-hidden="true" /> Choose…
                  </Button>
                </>
              )}
            </div>
            {openId === row.project_id ? (
              <div className="mt-2 rounded-lg border border-line bg-surface-2 p-2">
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search QuickBooks customers and jobs"
                  aria-label={`Search QuickBooks customers and jobs for ${row.name}`}
                  className="h-9 w-full rounded-md border border-line bg-surface px-2.5 text-[13px] text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  autoFocus
                />
                {!customers ? <p className="px-1 pt-2 text-[12px] text-ink-3">Loading…</p> : null}
                {customers && !matches.length ? <p className="px-1 pt-2 text-[12px] text-ink-3">No customer or job matches.</p> : null}
                <ul className="mt-1">
                  {matches.map((c) => (
                    <li key={c.qb_id}>
                      <button type="button" onClick={() => link(row, c.qb_id, c.full_name)} disabled={saving === row.project_id}
                        className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-[13px] text-ink hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                        <span className="truncate">{c.full_name}</span>
                        {c.is_active === false ? <span className="text-[11.5px] text-ink-4">inactive</span> : null}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function QbDesktopCard({ recheck = 0 }) {
  const confirm = useConfirm();
  const { feature, refresh: refreshFeatures } = useIntegrationStatus();
  const [status, setStatus] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState('');
  const [credentials, setCredentials] = useState(null);

  const load = useCallback(() => setTick((n) => n + 1), []);
  useEffect(() => {
    let alive = true;
    fetchQbStatus()
      .then((res) => { if (alive) { setStatus(res); setLoadError(''); } })
      .catch((err) => { if (alive) setLoadError(getErrorMessage(err, 'Could not read the QuickBooks Desktop status')); });
    return () => { alive = false; };
  }, [recheck, tick]);

  const on = status?.status === 'on';
  const label = feature(QB_DESKTOP_FEATURE).label || 'QuickBooks Desktop';
  const pill = qbDesktopPill(status);

  const switchTo = async (next) => {
    const run = async () => {
      setBusy('switch');
      try {
        await api.put(`/integrations/${QB_DESKTOP_FEATURE}`, { enabled: next });
      } finally {
        setBusy('');
      }
    };
    if (!next) {
      const ok = await confirm({
        title: `Switch ${label} off?`,
        description: 'The Web Connector gets “no work” until it is switched on again. What was already read stays.',
        confirmLabel: 'Switch off', tone: 'danger', confirmIcon: null, onConfirm: run,
      });
      if (!ok) return;
    } else {
      try {
        await run();
      } catch (err) {
        notify.error(`Could not switch ${label} on`, { description: getErrorMessage(err) });
        return;
      }
    }
    notify.success(`${label} is ${next ? 'on' : 'off'}`, { description: 'Recorded in History.' });
    refreshFeatures();
    load();
  };

  const makePassword = async () => {
    const create = async () => {
      setBusy('password');
      try {
        setCredentials(await newQbPassword());
        load();
      } finally {
        setBusy('');
      }
    };
    if (status?.set_up) {
      const ok = await confirm({
        title: 'Make a new Web Connector password?',
        description: 'The old password stops working at once — type the new one in the Web Connector.',
        confirmLabel: 'Make new password', confirmIcon: null, onConfirm: create,
      });
      if (!ok) return;
    } else {
      try {
        await create();
      } catch (err) {
        notify.error('Could not make the password', { description: getErrorMessage(err) });
      }
    }
  };

  const downloadQwc = async () => {
    setBusy('qwc');
    try {
      const blob = await fetchQwcFile();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'sara-pm-quickbooks.qwc';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      // the file is asked for as a blob, so a refusal's JSON detail arrives as a blob too
      let message = getErrorMessage(err);
      const data = err?.response?.data;
      if (typeof Blob !== 'undefined' && data instanceof Blob) {
        try { message = JSON.parse(await readBlobText(data)).detail || message; } catch { /* keep the generic text */ }
      } else if (data?.detail) {
        message = data.detail;
      }
      notify.error('Could not download the Web Connector file', { description: message });
    } finally {
      setBusy('');
    }
  };

  const resync = async () => {
    const ok = await confirm({
      title: 'Read everything from QuickBooks again?',
      description: 'The next Web Connector run reads every record again (it takes longer). Nothing is deleted meanwhile.',
      confirmLabel: 'Read everything again', confirmIcon: null,
      onConfirm: async () => { await resyncQb(); },
    });
    if (!ok) return;
    notify.success('The next run reads everything again');
    load();
  };

  const counts = status?.counts || {};
  const runs = status?.runs || [];
  return (
    <AdminCard
      title="QuickBooks Desktop"
      subtitle="Read only · invoices, payments, items, customers, vendors and costs from the company’s QuickBooks"
      icon={BookOpenCheck}
      iconTone="green"
      badge={pill ? <StatusPill tone={pill.tone} label={pill.label} size="sm" data-testid="qbd-state" /> : null}
      aside={on ? (
        <RowActions
          label="QuickBooks Desktop actions"
          items={[
            { label: 'Read everything again…', icon: RefreshCw, onSelect: resync, testId: 'qbd-resync' },
            { label: 'Switch off…', icon: Lock, danger: true, onSelect: () => switchTo(false), testId: 'qbd-switch-off' },
          ]}
        />
      ) : null}
      bodyClassName="space-y-5 py-5"
      data-testid="qbd-card"
    >
      {loadError ? (
        <p className="flex items-center gap-2 text-[13px] text-status-red-ink" data-testid="qbd-error">
          <TriangleAlert className="h-4 w-4" aria-hidden="true" /> {loadError}
          <Button type="button" variant="ghost" size="sm" onClick={load}><RefreshCw aria-hidden="true" /> Retry</Button>
        </p>
      ) : null}
      {!status && !loadError ? <div className="h-24 animate-pulse rounded-xl bg-surface-2" aria-label="Loading QuickBooks Desktop status" /> : null}

      {status ? (
        <>
          <AdminCallout tone="green" icon={ShieldCheck} className="py-3" data-testid="qbd-read-only">
            <p><b className="font-semibold text-ink">Sara never writes to QuickBooks.</b> It only asks QuickBooks questions, and the Web Connector file asks QuickBooks for read-only access. What QuickBooks says is shown next to Sara’s own numbers — nothing in Sara is overwritten.</p>
          </AdminCallout>

          {status.locked_out ? (
            <AdminCallout tone="red" icon={TriangleAlert} className="py-3" title="Too many wrong passwords" data-testid="qbd-locked">
              <p>The Web Connector is refused for 15 minutes. If the password in the Web Connector is wrong, make a new one below.</p>
            </AdminCallout>
          ) : null}

          {credentials ? <NewPassword credentials={credentials} onDone={() => setCredentials(null)} /> : null}

          <div>
            <UpperLabel>Setup</UpperLabel>
            <ol className="mt-1 divide-y divide-line-2" data-testid="qbd-steps">
              <Step
                index={1} title="Switch it on" done={on} testId="qbd-step-switch"
                detail={on ? 'On — the Web Connector gets work' : 'While off, the Web Connector is told there is nothing to do'}
                action={on ? <StatusPill tone="green" label="On" size="sm" /> : (
                  <Button type="button" size="sm" onClick={() => switchTo(true)} disabled={busy === 'switch'} data-testid="qbd-switch-on">
                    {busy === 'switch' ? <Loader2 className="animate-spin" aria-hidden="true" /> : null} Switch on
                  </Button>
                )}
              />
              <Step
                index={2} title="Web Connector password" done={status.set_up} testId="qbd-step-password"
                detail={status.set_up
                  ? `User ${status.username || 'sara-pm'}${status.password_set_at ? ` · made ${formatDateTime(status.password_set_at)}${status.password_set_by ? ` by ${status.password_set_by}` : ''}` : ''}`
                  : 'Made here, typed once in the Web Connector'}
                action={(
                  <Button type="button" variant={status.set_up ? 'ghost' : 'primary'} size="sm" onClick={makePassword} disabled={busy === 'password'} data-testid="qbd-make-password">
                    {busy === 'password' ? <Loader2 className="animate-spin" aria-hidden="true" /> : <KeyRound aria-hidden="true" />}
                    {status.set_up ? 'New password…' : 'Make password'}
                  </Button>
                )}
              />
              <Step
                index={3} title="Web Connector file (.qwc)" done={Boolean(status.last_success_at)} locked={!status.set_up} testId="qbd-step-file"
                detail="Asks QuickBooks for read-only access · runs every 30 minutes"
                action={(
                  <Button type="button" variant="outline" size="sm" onClick={downloadQwc} disabled={!status.set_up || busy === 'qwc'} data-testid="qbd-download-qwc">
                    {busy === 'qwc' ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Download aria-hidden="true" />} Download
                  </Button>
                )}
              />
              <Step
                index={4} title="On the QuickBooks PC" done={Boolean(status.last_success_at)} locked={!status.set_up} testId="qbd-step-pc"
                detail={(
                  <ol className="mt-1 list-decimal space-y-0.5 pl-4 text-[12.5px] text-ink-2" data-testid="qbd-pc-steps">
                    <li>Install the free <b className="font-semibold">QuickBooks Web Connector</b> from Intuit on the PC that has QuickBooks and the company file.</li>
                    <li>Open QuickBooks as Admin (single-user the first time) with the company file.</li>
                    <li>In the Web Connector: <b className="font-semibold">Add an application</b> › choose the .qwc file.</li>
                    <li>QuickBooks asks for access: <b className="font-semibold">“Yes, always; allow access even if QuickBooks is not running”</b> and pick the QuickBooks user.</li>
                    <li>Type the password in the <b className="font-semibold">Password</b> column, tick the app, turn on Auto-Run and press <b className="font-semibold">Update Selected</b>.</li>
                  </ol>
                )}
              />
            </ol>
          </div>

          <div data-testid="qbd-read">
            <UpperLabel>What Sara has read</UpperLabel>
            {status.last_success_at ? (
              <>
                <p className="mt-1 text-[13px] text-ink-2">
                  {status.company_name ? <b className="font-semibold text-ink">{status.company_name}</b> : 'QuickBooks'}
                  {' · last read '}{formatDateTime(status.last_success_at)}
                  {status.product ? <span className="text-ink-3"> · {status.product}</span> : null}
                </p>
                <dl className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {KIND_LABELS.map(([kind, text]) => (
                    <div key={kind} className="rounded-lg border border-line px-3 py-2" data-testid={`qbd-count-${kind}`}>
                      <dt className="text-[11.5px] text-ink-3">{text}</dt>
                      <dd className="font-heading text-[18px] font-bold tabular-nums text-ink">{formatNumber(counts[kind] || 0)}</dd>
                    </div>
                  ))}
                </dl>
              </>
            ) : (
              <p className="mt-1 flex items-center gap-1.5 text-[13px] text-ink-3"><MonitorCog className="h-4 w-4" aria-hidden="true" /> Nothing read yet — finish the steps above; the first run reads everything.</p>
            )}
            {status.last_refusal?.message && !on ? (
              <p className="mt-2 text-[12px] text-status-amber-ink">The Web Connector tried {formatTimeAgo(status.last_refusal.at)} while this was off.</p>
            ) : null}
          </div>

          {runs.length ? (
            <div>
              <UpperLabel>Last runs</UpperLabel>
              <ul className="mt-2 divide-y divide-line-2 rounded-xl border border-line" data-testid="qbd-runs">
                {runs.map((run) => {
                  const runPill = RUN_PILL[run.status] || RUN_PILL.running;
                  const total = Object.values(run.counts || {}).reduce((sum, n) => sum + Number(n || 0), 0);
                  const errors = Object.entries(run.step_errors || {});
                  return (
                    <li key={run.id || run.started_at} className="px-3 py-2.5 text-[12.5px]">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="text-ink">{formatDateTime(run.started_at)}</span>
                        <StatusPill tone={runPill.tone} label={runPill.label} size="sm" />
                        <span className="text-ink-3">{formatNumber(total)} records{run.full ? ' · full read' : ''}</span>
                      </div>
                      {run.message ? <p className="mt-1 text-status-red-ink">{run.message}</p> : null}
                      {errors.map(([step, text]) => <p key={step} className="mt-1 text-status-amber-ink">{step}: {text}</p>)}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

          {status.last_success_at ? <ProjectLinks reloadKey={`${recheck}-${tick}`} /> : null}
        </>
      ) : null}
    </AdminCard>
  );
}
